import { db } from '../config/firebase';
import { collection, doc, setDoc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { Business } from '../types/business';

/**
 * Generate URL-friendly slug from business name
 */
export const generateSlug = (name: string): string => {
    return name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .trim()
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-'); // Remove consecutive hyphens
};

/**
 * Generate a unique invite code (8 caracteres, sin caracteres confusos)
 */
export const generateInviteCode = (): string => {
    // Excluye 0, O, I, 1 para evitar confusiones
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
};

/**
 * Generate full invite link with business slug (limpio, sin código visible)
 */
export const generateInviteLink = (slug: string, _inviteCode?: string): string => {
    const baseUrl = window.location.origin;
    // URL limpia: /b/slug (nuevo formato)
    return `${baseUrl}/b/${slug}`;
};

/**
 * Validate if an invite code exists and is valid
 */
export const validateInviteCode = async (inviteInput: string): Promise<Business | null> => {
    try {
        // Acepta URL completa, código o slug
        let code = '';
        let slug = '';
        if (inviteInput.startsWith('http')) {
            const url = new URL(inviteInput);
            code = url.searchParams.get('invite') || url.searchParams.get('code') || '';
            const parts = url.pathname.split('/').filter(Boolean);
            const regIdx = parts.findIndex(p => p === 'register');
            if (regIdx >= 0 && parts.length > regIdx + 1) {
                slug = parts[regIdx + 1];
            }
        } else {
            // Si no es URL, puede ser código o slug. Intentar ambos
            slug = inviteInput.trim();
            code = inviteInput.trim();
        }

        const businessesRef = collection(db, 'businesses');

        // 1) Intentar por código
        if (code) {
            const q = query(businessesRef, where('inviteCode', '==', code));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const docRef = snap.docs[0];
                const data = docRef.data() as Business;
                if (data.subscription.status !== 'active') {
                    throw new Error('Este negocio no tiene una suscripción activa');
                }
                return {
                    ...data,
                    id: docRef.id,
                    createdAt: data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt),
                    updatedAt: data.updatedAt instanceof Date ? data.updatedAt : new Date(data.updatedAt),
                };
            }
        }

        // 2) Intentar por slug
        if (slug) {
            const q2 = query(businessesRef, where('slug', '==', slug));
            const snap2 = await getDocs(q2);
            if (!snap2.empty) {
                const docRef = snap2.docs[0];
                const data = docRef.data() as Business;
                if (data.subscription.status !== 'active') {
                    throw new Error('Este negocio no tiene una suscripción activa');
                }
                return {
                    ...data,
                    id: docRef.id,
                    createdAt: data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt),
                    updatedAt: data.updatedAt instanceof Date ? data.updatedAt : new Date(data.updatedAt),
                };
            }
        }

        return null;
    } catch (error) {
        console.error('Error validating invite code:', error);
        throw error;
    }
};

/**
 * Get business by ID
 */
export const getBusinessById = async (businessId: string): Promise<Business | null> => {
    try {
        const businessRef = doc(db, 'businesses', businessId);
        const businessSnap = await getDoc(businessRef);

        if (!businessSnap.exists()) {
            return null;
        }

        const businessData = businessSnap.data() as Business;
        return {
            ...businessData,
            id: businessSnap.id,
            createdAt: businessData.createdAt instanceof Date ? businessData.createdAt : new Date(businessData.createdAt),
            updatedAt: businessData.updatedAt instanceof Date ? businessData.updatedAt : new Date(businessData.updatedAt),
        };
    } catch (error) {
        console.error('Error getting business:', error);
        throw error;
    }
};

/**
 * Generate clean slug (sin sufijo aleatorio)
 */
const getUniqueSlug = async (baseSlug: string): Promise<string> => {
    // Slug limpio, solo el nombre del negocio
    return baseSlug;
};

/**
 * Create a new business
 */
export const createBusiness = async (
    businessData: { name: string; adminEmail: string; phone?: string; address?: string },
    adminUserId: string
): Promise<Business> => {
    try {
        const inviteCode = generateInviteCode();
        const baseSlug = generateSlug(businessData.name);
        const slug = await getUniqueSlug(baseSlug);
        const inviteLink = generateInviteLink(slug, inviteCode);

        const newBusiness: Omit<Business, 'id'> = {
            name: businessData.name,
            slug,
            adminEmail: businessData.adminEmail,
            adminUserId,
            inviteCode,
            inviteLink,
            logo: '',
            address: businessData.address || '',
            phone: businessData.phone || '',
            settings: {
                timezone: 'Europe/Madrid',
                currency: 'EUR',
                language: 'es',
            },
            subscription: {
                plan: 'free',
                status: 'active',
            },
            createdAt: new Date(),
            updatedAt: new Date(),
        };

        const businessRef = doc(collection(db, 'businesses'));
        await setDoc(businessRef, newBusiness);

        return {
            ...newBusiness,
            id: businessRef.id,
        };
    } catch (error) {
        console.error('Error creating business:', error);
        throw error;
    }
};

/**
 * Regenerate invite link for a business
 */
export const regenerateInviteLink = async (businessId: string): Promise<string> => {
    try {
        // Get current business to retrieve slug
        const business = await getBusinessById(businessId);
        if (!business) throw new Error('Business not found');

        const newInviteCode = generateInviteCode();
        const newInviteLink = generateInviteLink(business.slug, newInviteCode);

        const businessRef = doc(db, 'businesses', businessId);
        await setDoc(businessRef, {
            inviteCode: newInviteCode,
            inviteLink: newInviteLink,
            updatedAt: new Date(),
        }, { merge: true });

        return newInviteLink;
    } catch (error) {
        console.error('Error regenerating invite link:', error);
        throw error;
    }
};
